import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clearLastExecution, getBffQueryData, useQueryLPC } from 'app/lib/query';
import { bffUserProfileLoadedAtom, isLoggedInAtom } from 'app/atoms';
import type { FunctionalUseQueryOptions } from 'app/types/network';
import { isPast } from 'date-fns';
import { useAtomValue } from 'jotai';
import { http } from '../lib/axios';
import { trackOnFacebook, CustomFacebookEvent } from 'app/lib/analytics';
import type { Channel } from 'app/types/chat';
import type { Listing } from 'app/types/listing';
import { loanKeys } from './keys';
import { useQueryKeys } from './invalidation';
import {
  type Loan,
  type FindAllLoansDTO,
  LoanStatus,
  LoanType,
  type CreateLoanDTO,
  type CreateLoanParticipationDTO,
  type RateLoanPayload,
} from 'app/types/loan';
import { useSelectedGroupsAndAddresses } from 'app/network/listing';
import { mergeListingDetailCache } from 'app/network/listing-cache';
import type { Profile } from 'app/types/profile';

export { LoanType } from 'app/types/loan';

export function useAcceptOneLoan(opts: UseMutationOptions<Loan, Error, Loan['id']> = {}) {
  const client = useQueryClient();
  const qk = useQueryKeys();

  return useMutation({
    mutationFn: (id: Loan['id']) => http.post<Loan>(`/loans/${id}/accept`).then((res) => res.data),
    ...opts,
    onSuccess(updatedLoan, id, context) {
      void qk.afterLoanAccepted(updatedLoan);
      client.setQueryData(loanKeys.detail(id), updatedLoan);
      trackOnFacebook(CustomFacebookEvent.AcceptedLoan);
      return opts.onSuccess?.(updatedLoan, id, context);
    },
  });
}

export enum LoanFilterKey {
  Management = 'management',
  PastLoans = 'pastLoans',
  Completed = 'completed',
  Accepted = 'accepted',
}

const loanFilterStatus: Record<LoanFilterKey, (loanType?: LoanType) => LoanStatus[]> = {
  management: (loanType) =>
    loanType === LoanType.Lending ? [LoanStatus.Accepted, LoanStatus.Returned] : [LoanStatus.Accepted],
  pastLoans: (loanType) =>
    loanType === LoanType.Lending ? [LoanStatus.Completed] : [LoanStatus.Completed, LoanStatus.Returned],
  completed: () => [LoanStatus.Completed],
  accepted: () => [LoanStatus.Accepted],
};

export function getAllLoansFilters(key: keyof typeof loanFilterStatus, loanType?: LoanType) {
  return {
    status: loanFilterStatus[key](loanType),
    type: loanType,
  };
}

export function useAllLoans(
  key: keyof typeof loanFilterStatus,
  loanType?: LoanType,
  opts: FunctionalUseQueryOptions<Loan[]> = {},
) {
  const client = useQueryClient();
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const bffUserProfileLoaded = useAtomValue(bffUserProfileLoadedAtom);

  const filters = getAllLoansFilters(key, loanType);
  const queryKey = loanKeys.list('self', filters);
  return useQueryLPC<Loan[]>({
    queryKey,
    queryFn: async () => {
      let resData =
        (bffUserProfileLoaded ? getBffQueryData<Loan[]>(client, queryKey) : undefined) ??
        (await http.get<Loan[]>('/loans', { params: filters })).data;

      resData.forEach((loan) => client.setQueryData(loanKeys.detail(loan.id), loan));

      // Sort by start date (if it is in the future), returned date, or end date (when returned date is empty), ascending
      resData = resData.sort(
        (a, b) =>
          new Date(!isPast(a.start) ? a.start : (a.returned ?? a.end)).getTime() -
          new Date(!isPast(b.start) ? b.start : (b.returned ?? b.end)).getTime(),
      );

      return resData;
    },
    ...opts,
    enabled: (opts.enabled ?? true) && isLoggedIn && bffUserProfileLoaded,
  });
}

export function useCreateOneLoan(opts: UseMutationOptions<Loan, Error, CreateLoanDTO> = {}) {
  const client = useQueryClient();
  const qk = useQueryKeys();

  return useMutation({
    mutationFn: (payload) => http.post<Loan>('/loans', payload).then((res) => res.data),
    ...opts,
    async onSuccess(result, variables, context) {
      await Promise.all([
        qk.allLoans.invalidate(),
        ...(variables.channelURL ? [qk.channel.invalidate(variables.channelURL)] : []),
      ]);
      client.setQueryData(loanKeys.detail(result.id), result);
      trackOnFacebook(CustomFacebookEvent.OfferedLoan);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useCreateOneLoanParticipation(
  opts: UseMutationOptions<Listing, Error, CreateLoanParticipationDTO> = {},
) {
  const client = useQueryClient();
  const qk = useQueryKeys();

  return useMutation({
    mutationFn: (payload) => http.post<Listing>('/loans/participations', payload).then((res) => res.data),
    ...opts,
    async onSuccess(result, variables, context) {
      await qk.listing.invalidate(result.id);
      mergeListingDetailCache(client, result);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useOneLoan(id?: Loan['id'], opts: FunctionalUseQueryOptions<Loan | null> = {}) {
  return useQueryLPC<Loan | null>({
    queryKey: loanKeys.detail(id ?? 'meh'),
    queryFn: () => (!id ? null : http.get<Loan>(`/loans/${id}`).then((res) => res.data)),
    ...opts,
    enabled: (opts.enabled ?? true) && Boolean(id),
  });
}

export function useRejectOneLoan(opts: UseMutationOptions<Loan, Error, Loan['id']> = {}) {
  const client = useQueryClient();
  const qk = useQueryKeys();

  return useMutation({
    mutationFn: (id: Loan['id']) => http.post<Loan>(`/loans/${id}/reject`).then((res) => res.data),
    ...opts,
    onSuccess(result, id, context) {
      void qk.afterLoanRejected(result);
      client.setQueryData(loanKeys.detail(id), result);
      return opts.onSuccess?.(result, id, context);
    },
  });
}

export function useRateLoanMutation(
  channel: Pick<Channel, 'channel_url'>,
  opts: UseMutationOptions<Loan['rating'], Error, { id: Loan['id'] } & RateLoanPayload> = {},
) {
  const qk = useQueryKeys();
  return useMutation({
    mutationFn: ({ id, rating, ratings, comment }) =>
      http
        .post(`/loans/${id}/rating`, {
          ...(rating !== undefined ? { rating } : {}),
          ...(ratings?.length ? { ratings } : {}),
          ...(comment !== undefined ? { comment } : {}),
        })
        .then((res) => res.data),
    ...opts,
    async onSuccess(result, req, context) {
      clearLastExecution();
      await Promise.all([qk.channel.invalidate(channel.channel_url), qk.allLoans.invalidate()]);
      return opts.onSuccess?.(result, req, context);
    },
  });
}

export function useDeleteRating(opts: UseMutationOptions<undefined, Error, string> = {}) {
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, string>({
    mutationFn: (loanId: string) => http.delete(`/loans/${loanId}/rating`).then(() => undefined),
    ...opts,
    onSuccess: async (data, variables, context) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: loanKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: loanKeys.ratings() }),
      ]);
      return opts.onSuccess?.(data, variables, context);
    },
  });
}

export function useReportRating(opts: UseMutationOptions<undefined, Error, string> = {}) {
  return useMutation<undefined, Error, string>({
    mutationFn: (loanId: string) => http.post(`/loans/${loanId}/rating/report`).then(() => undefined),
    ...opts,
  });
}

export function useCompleteOneLoan(opts: UseMutationOptions<Loan, Error, Loan['id']> = {}) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  return useMutation({
    mutationFn: (id) => http.post(`/loans/${id}/complete`).then((res) => res.data),
    ...opts,
    onSuccess(result, id, context) {
      void qk.afterLoanCompleted(result);
      client.setQueryData(loanKeys.detail(id), result);
      return opts.onSuccess?.(result, id, context);
    },
  });
}

interface RecentLoanUserDTO {
  id: string;
  profile?: { firstName: string; picture: string | null };
}

type RecentLoanListingDTO = Pick<
  Listing,
  'id' | 'picture' | 'type' | 'subtype' | 'item' | 'categoryPrimary' | 'pictures' | 'transactionMode' | 'isDonation'
> & {
  before: string | null;
};

interface RecentLoanDTO {
  id: string;
  end: string;
  returned: string | null;
  listing: RecentLoanListingDTO;
  borrower: RecentLoanUserDTO;
  lender: RecentLoanUserDTO;
}

/** A Listing augmented with loan borrower/lender info, for the recent loans card. */
export type RecentLoanListing = RecentLoanListingDTO & {
  borrower: RecentLoanUserDTO;
  lender: RecentLoanUserDTO;
  completedAt: string;
};

export function useRecentLoans(opts: FunctionalUseQueryOptions<RecentLoanListing[]> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();

  const params = {
    addressIds: selectedAddresses,
    groupIds: selectedGroups,
  };

  const queryKey = loanKeys.list('recent', params as unknown as FindAllLoansDTO);
  return useQueryLPC<RecentLoanListing[]>({
    queryKey,
    queryFn: () =>
      http.get<RecentLoanDTO[]>('/loans/recent', { params }).then((res) =>
        res.data.map((loan) => ({
          ...loan.listing,
          borrower: loan.borrower,
          lender: loan.lender,
          completedAt: loan.returned ?? loan.end,
        })),
      ),
    ...opts,
    enabled: (opts.enabled ?? true) && isLoggedIn && loaded,
  });
}

export interface RatingsFilters {
  userId?: string;
  listingId?: string;
  reviewerId?: string;
}

export function useRatings(
  { userId, listingId, reviewerId }: RatingsFilters,
  opts: FunctionalUseQueryOptions<NonNullable<Profile['ratings']>> = {},
) {
  return useQueryLPC<NonNullable<Profile['ratings']>>({
    queryKey: loanKeys.ratings({ userId, listingId, reviewerId }),
    queryFn: () =>
      http
        .get<NonNullable<Profile['ratings']>>('/loans/ratings', {
          params: {
            ...(userId ? { userId } : {}),
            ...(listingId ? { listingId } : {}),
            ...(reviewerId ? { reviewerId } : {}),
          },
        })
        .then((res) => res.data),
    ...opts,
    enabled: (opts.enabled ?? true) && Boolean(userId || listingId || reviewerId),
  });
}
